// src/components/PrototypePanel.tsx (v2.19)
// 「予算・前年比較」 タブ メインコンテナ:
//   1. KPI Cards   (6×3 = 18 spec、 tableOnly 指標は除外)
//   2. Mini Charts (6×3 = 18 spec、 月次推移 / 実績・予算・前年同月)
//   3. Summary Table (20 行 × 月別マトリックス、 表のみ指標 薬剤材料費 / 病床利用率 を含む)
//   4. DeptMetricTable (注目指標 × 科別 × 月、 対前年 / 対予算 トグル付き)
// 入院実績タブと同じ階層感 (cards → charts → table) で 1 fetch (dept_metrics_all) を共有。
// 派生 4 指標 (薬剤材料費 / 入院延数÷医師÷日 / 在院延数÷医師÷日 / 1日粗利単価(在院)) は client 側で base 値から導出。
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useFilters } from "../store";
import type { DeptMetricsAllResponse, MetricRow } from "@shared/api";
import { ContextBar } from "./ContextBar";
import { BudgetKpiGrid } from "./prototypes/BudgetKpiGrid";
import { BudgetTrendGrid } from "./prototypes/BudgetTrendGrid";
import { AllMetricsTable } from "./prototypes/AllMetricsTable";
import { DeptMetricTable } from "./DeptMetricTable";
import { resolveAllSpecs } from "./prototypes/budgetSpec";
import { useAuth } from "../lib/useAuth";
import { fetchDoctorDetail } from "../lib/inpatientDoctorDetail";
import { ymJp } from "../lib/format";

interface Props { metrics: MetricRow[]; builtAt: string; }

export function PrototypePanel({ metrics, builtAt }: Props) {
  const f = useFilters();
  const { canViewDoctor } = useAuth();
  const doctor = canViewDoctor ? f.admdrcd : null;
  const avgMode = f.periodPreset === "直近12ヶ月平均";
  const defaultTarget = f.focusedDept ?? "病院全体";
  const targetIcon = f.focusedDept ? "🎯" : "🏥";

  const ymLabel = `${f.asOfMonth.slice(2, 4)}年${f.asOfMonth.slice(5, 7)}月`;

  // Codex 指摘 #4 反映: queryKey に f.periodTo を含める (現状 periodTo === asOfMonth なので
  // 機能的には no-op だが、 将来 custom 期間で divergence したときに stale cache を避ける defensive 措置)。
  // 直近12ヶ月平均モードはマトリックス/チャートを12ヶ月移動平均にするため、 trend を24ヶ月に拡張 (trendBack=23)。
  const trendBack = avgMode ? 23 : 11;
  const { data, isFetching, error } = useQuery<DeptMetricsAllResponse>({
    queryKey: ["dept_metrics_all", f.granularity, f.iryo, f.yotei, f.asOfMonth, f.periodFrom, f.periodTo, f.focusedDept, trendBack, doctor],
    queryFn: () => {
      const filters = {
      granularity: f.granularity, iryo: f.iryo, yotei: f.yotei,
      asOfMonth: f.asOfMonth, periodFrom: f.periodFrom,
      focusedDept: f.focusedDept ?? undefined, trendBack,
      };
      return doctor ? fetchDoctorDetail(filters, doctor) : api.deptMetricsAll(filters);
    },
  });

  const targetName = doctor ? (data?.diagnosedDept ?? "選択した医師") : defaultTarget;
  const targetLabel = `${doctor ? "👨‍⚕️" : targetIcon} ${targetName}`;
  const resolved = useMemo(() => data ? resolveAllSpecs(data).map(r => doctor && r.spec.key === "平均在院日数"
    ? { ...r, spec: { ...r.spec, label: "平均受持日数" } } : r) : [], [data, doctor]);

  return (
    <div className="space-y-3">
      <section className="card p-3 no-print">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-base font-semibold">📊 入院実績</h2>
          <span className="text-xs text-slate-500">
            {targetName} の実績：カード・月次推移・月別マトリックス
          </span>
          <span className="text-xs text-slate-500 ml-auto">
            対象: <b className={f.focusedDept ? "text-amber-700" : "text-slate-700"}>{targetLabel}</b>
            {" "}/ 対象月 <b>{ymJp(f.asOfMonth)}</b>
            {" "}/ 期間 <b>{ymJp(f.periodFrom)}</b> 〜 <b>{ymJp(f.periodTo)}</b>
            {isFetching && <span className="ml-2 text-slate-400">loading…</span>}
          </span>
        </div>
      </section>

      {error && (
        <div className="card p-4 text-sm text-red-700 bg-red-50">
          データ取得エラー: {(error as Error).message}
        </div>
      )}

      {doctor && <div className="card p-3 text-xs text-slate-600">
        対象医師：{targetName}{f.focusedDept ? `（${f.focusedDept}）` : "（全診療科）"}。
        その在院日の主治医で集計。新規受持数は新規入院＋他医師からの受持替え、平均受持日数は受持替えを含む日数です。
        医師別予算・常勤医師数・紹介入院率・薬剤材料費・病床利用率は「—」表示です。
      </div>}
      {data && !isFetching && (
        <>
          {/* 各 div.budget-print-page が印刷時 1 ページに対応 (A4 横 × 3 ページ)。
              Page 1: Cards + Charts (統合)。 Page 2: Monthly Matrix。 Page 3: DeptMetricTable。
              CSS は styles.css の `.budget-print-page + .budget-print-page` 参照。 */}
          <div className="budget-print-page space-y-3">
            <ContextBar forPrint metrics={metrics} builtAt={builtAt} />
            <BudgetKpiGrid resolved={resolved} targetName={targetLabel} ymLabel={ymLabel} avgMode={avgMode} />
            <BudgetTrendGrid resolved={resolved} targetName={targetLabel}
                             periodFromYM={f.periodFrom.slice(0, 7)} asOfYM={f.asOfMonth.slice(0, 7)} avgMode={avgMode} />
          </div>
          <div className="budget-print-page">
            <ContextBar forPrint metrics={metrics} builtAt={builtAt} />
            <AllMetricsTable resolved={resolved} targetName={targetLabel} avgMode={avgMode} />
          </div>
          {!doctor && <div className="budget-print-page">
            <ContextBar forPrint metrics={metrics} builtAt={builtAt} />
            <DeptMetricTable metrics={metrics} showBudget />
          </div>}
        </>
      )}
    </div>
  );
}
